import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Card,
  Divider,
  InputLabel,
  Alert,
  Snackbar,
  useMediaQuery,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import { useCookies } from "react-cookie";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconSidenav from "../../common/IconSidenav";
import JoditEditor from "jodit-react";

const EditPeople = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get person ID from URL params
  const peopleDataFromState = location.state?.peopleData;

  const [peopleDetails, setPeopleDetails] = useState({
    name: "",
    position: "",
    email: "",
    qualifications: "",
    description: "",
    fullProfile1: "",
    fullProfile2: "",
    fullProfile3: "",
    ogUrl: "",
    category: "",
    categoryUrl: "",
    imageUrl: "",
  });

  const [cookies] = useCookies();
  const [personImage, setPersonImage] = useState(null);
  const [selectedPersonImage, setSelectedPersonImage] = useState("");
  const [personImageChanged, setPersonImageChanged] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [showSideNav, setShowSideNav] = useState(false);
  const isMobileScreen = useMediaQuery(
    "(min-width:360px) and (max-width:500px)"
  );
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!peopleDataFromState);

  // Category options
  const categoryOptions = [
    "Faculty",
    "Staff",
    "Management",
    "Research",
    "Administration",
    "Technical",
    "Other"
  ];

  const toggleSideNav = () => {
    setShowSideNav(!showSideNav);
  };

  // Fetch people data if not passed via state
  useEffect(() => {
    const fetchPeopleData = async () => {
      if (!peopleDataFromState && id) {
        try {
          setFetching(true);
          const params = { id: id };
          const response = await invokeApi(
            config.getMyCollege + apiList.getPerson,
            params,
            cookies
          );

          if (response?.status === 200) {
            const peopleData = response.data.person;
            setPeopleDetails({
              name: peopleData.name || "",
              position: peopleData.position || "",
              email: peopleData.email || "",
              qualifications: peopleData.qualifications || "",
              description: peopleData.description || "",
              fullProfile1: peopleData.fullProfile1 || peopleData.full_profile1 || "",
              fullProfile2: peopleData.fullProfile2 || peopleData.full_profile2 || "",
              fullProfile3: peopleData.fullProfile3 || peopleData.full_profile3 || "",
              ogUrl: peopleData.ogUrl || peopleData.og_url || "",
              category: peopleData.category || "",
              categoryUrl: peopleData.categoryUrl || peopleData.category_url || "",
              imageUrl: peopleData.imageUrl || peopleData.image_url || "",
            });
            setSelectedPersonImage(peopleData.imageUrl || peopleData.image_url || "");
          } else {
            showSnackbar("Failed to fetch person data", "error");
            navigate("/people-manager/people-list");
          }
        } catch (error) {
          console.error("Error fetching person data:", error);
          showSnackbar("Error fetching person data", "error");
          navigate("/people-manager/people-list");
        } finally {
          setFetching(false);
        }
      } else if (peopleDataFromState) {
        // Use data from state if available
        setPeopleDetails({
          name: peopleDataFromState.name || "",
          position: peopleDataFromState.position || "",
          email: peopleDataFromState.email || "",
          qualifications: peopleDataFromState.qualifications || "",
          description: peopleDataFromState.description || "",
          fullProfile1: peopleDataFromState.fullProfile1 || peopleDataFromState.full_profile1 || "",
          fullProfile2: peopleDataFromState.fullProfile2 || peopleDataFromState.full_profile2 || "",
          fullProfile3: peopleDataFromState.fullProfile3 || peopleDataFromState.full_profile3 || "",
          ogUrl: peopleDataFromState.ogUrl || peopleDataFromState.og_url || "",
          category: peopleDataFromState.category || "",
          categoryUrl: peopleDataFromState.categoryUrl || peopleDataFromState.category_url || "",
          imageUrl: peopleDataFromState.imageUrl || peopleDataFromState.image_url || "",
        });
        setSelectedPersonImage(peopleDataFromState.imageUrl || peopleDataFromState.image_url || "");
      }
    };

    fetchPeopleData();
  }, [id, peopleDataFromState, cookies, navigate]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setPeopleDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const handleInputContentChange = (name, value) => {
    const plainText = stripHtmlTags(value);
    setPeopleDetails((prevState) => ({
      ...prevState,
      [name]: plainText,
    }));
  };

  const handlePersonImage = (files) => {
    if (files && files.length > 0) {
      setPersonImageChanged(true);
      const selectedPersonFile = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedPersonImage(reader.result);
        setPersonImage(selectedPersonFile);
      };
      reader.readAsDataURL(selectedPersonFile);
    } else {
      setSelectedPersonImage("");
      setPersonImage(null);
      setPersonImageChanged(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleAddPersonImage = async () => {
    if (!personImage) return peopleDetails.imageUrl;

    const formData = new FormData();
    formData.append("profile", personImage);
    try {
      const response = await fetch(
        "https://api.reef.org.in/addProfileImage",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response?.status >= 200 && response?.status < 300) {
        const responseData = await response.json();
        return responseData.imageUrl || "";
      } else {
        showSnackbar("Failed to add person image", "error");
        return peopleDetails.imageUrl;
      }
    } catch (error) {
      showSnackbar("Something went wrong. Please try again later.", "error");
      return peopleDetails.imageUrl;
    }
  };

  const handleUpdatePerson = async (updatedPeopleData) => {
    try {
      setLoading(true);

      const response = await invokeApi(
        config.getMyCollege + apiList.updatePerson,
        updatedPeopleData,
        cookies
      );

      // If we get any 2xx response, consider it successful
      if (response?.status >= 200 && response?.status < 300) {
        showSnackbar("Person updated successfully", "success");
        navigate("/people-manager/people-list");
      } else {
        showSnackbar("Update failed. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during updating person:", error);
      showSnackbar("Something went wrong. Please try again later!!", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePeopleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (!peopleDetails.name?.trim()) {
      showSnackbar("Name is required", "error");
      return;
    }

    if (!peopleDetails.position?.trim()) {
      showSnackbar("Position is required", "error");
      return;
    }

    // Email validation
    if (peopleDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(peopleDetails.email)) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    try {
      const imageUrl = personImageChanged
        ? await handleAddPersonImage()
        : peopleDetails.imageUrl;

      const updatedPeopleData = {
        id: id,
        name: peopleDetails.name.trim(),
        position: peopleDetails.position.trim(),
        email: peopleDetails.email.trim() || "",
        qualifications: peopleDetails.qualifications || "",
        description: peopleDetails.description || "",
        fullProfile1: peopleDetails.fullProfile1 || "",
        fullProfile2: peopleDetails.fullProfile2 || "",
        fullProfile3: peopleDetails.fullProfile3 || "",
        ogUrl: peopleDetails.ogUrl || "",
        category: peopleDetails.category || "",
        categoryUrl: peopleDetails.categoryUrl || "",
        imageUrl: imageUrl,
      };

      await handleUpdatePerson(updatedPeopleData);

    } catch (error) {
      console.error("Error while updating person details:", error);
      showSnackbar("Something went wrong. Please try again later.", "error");
    }
  };

  if (fetching) {
    return (
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? <IconSidenav /> : showSideNav ? <IconSidenav /> : <Sidenav />}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />

          <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
            <Card elevation={1} sx={{ my: 5 }}>
              <Grid sx={{ py: isMobileScreen ? 2 : 2 }}>
                <Typography
                  sx={{
                    mb: 2,
                    mx: isMobileScreen ? 2 : 8,
                    textAlign: "left",
                    fontSize: isMobileScreen ? 14 : 18,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Edit Person
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    my: 2,
                  }}
                >
                  {/* Basic Information */}
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Name *
                    </InputLabel>
                    <TextField
                      placeholder="Enter Name"
                      fullWidth
                      name="name"
                      value={peopleDetails.name}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Position *
                    </InputLabel>
                    <TextField
                      placeholder="Enter Position"
                      fullWidth
                      name="position"
                      value={peopleDetails.position}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Email
                    </InputLabel>
                    <TextField
                      placeholder="Enter Email Address"
                      fullWidth
                      name="email"
                      type="email"
                      value={peopleDetails.email}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Qualifications
                    </InputLabel>
                    <TextField
                      placeholder="Enter Qualifications (e.g., PhD, MSc, B.Tech)"
                      fullWidth
                      name="qualifications"
                      value={peopleDetails.qualifications}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Category
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        name="category"
                        value={peopleDetails.category}
                        onChange={handleInputChange}
                        displayEmpty
                        inputProps={{
                          style: {
                            height: isMobileScreen ? "35px" : "40px",
                            fontSize: isMobileScreen ? 12 : 14,
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select Category</em>
                        </MenuItem>
                        {categoryOptions.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Description */}
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Description
                    </InputLabel>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        my: isMobileScreen ? 2 : 2,
                        mb: isMobileScreen ? 15 : 1,
                      }}
                    >
                      <JoditEditor
                        value={peopleDetails.description}
                        onChange={(newValue) =>
                          handleInputContentChange("description", newValue)
                        }
                        tabIndex={1}
                      />
                    </Grid>
                  </Grid>

                  {/* Full Profile Sections */}
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Full Profile Section 1
                    </InputLabel>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        my: isMobileScreen ? 2 : 2,
                        mb: isMobileScreen ? 15 : 1,
                      }}
                    >
                      <JoditEditor
                        value={peopleDetails.fullProfile1}
                        onChange={(newValue) =>
                          handleInputContentChange("fullProfile1", newValue)
                        }
                        tabIndex={2}
                      />
                    </Grid>
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Full Profile Section 2
                    </InputLabel>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        my: isMobileScreen ? 2 : 2,
                        mb: isMobileScreen ? 15 : 1,
                      }}
                    >
                      <JoditEditor
                        value={peopleDetails.fullProfile2}
                        onChange={(newValue) =>
                          handleInputContentChange("fullProfile2", newValue)
                        }
                        tabIndex={3}
                      />
                    </Grid>
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Full Profile Section 3
                    </InputLabel>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        my: isMobileScreen ? 2 : 2,
                        mb: isMobileScreen ? 15 : 1,
                      }}
                    >
                      <JoditEditor
                        value={peopleDetails.fullProfile3}
                        onChange={(newValue) =>
                          handleInputContentChange("fullProfile3", newValue)
                        }
                        tabIndex={4}
                      />
                    </Grid>
                  </Grid>

                  {/* URLs and Additional Fields */}
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      OG URL
                    </InputLabel>
                    <TextField
                      placeholder="Enter Open Graph URL"
                      fullWidth
                      name="ogUrl"
                      value={peopleDetails.ogUrl}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Category URL
                    </InputLabel>
                    <TextField
                      placeholder="Enter Category URL"
                      fullWidth
                      name="categoryUrl"
                      value={peopleDetails.categoryUrl}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  {/* Image Upload Section */}
                  <Grid
                    container
                    md={12}
                    sx={{
                      width: "100%",
                      justifyContent: "center",
                      my: 5,
                    }}
                  >
                    <Grid item md={5} xs={11}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          my: 1,
                          fontSize: isMobileScreen ? 12 : 15,
                        }}
                      >
                        Person Image
                      </InputLabel>
                      <Grid
                        sx={{
                          my: 2,
                          ".MuiBox-root ": {
                            bgcolor: "#f0f8ff",
                            borderColor: "#1d1a57",
                            minHeight: 0,
                            my: 1,
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        <DropzoneArea
                          filesLimit={1}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          onChange={handlePersonImage}
                          maxFileSize={5000000}
                          showPreviews={true}
                          showAlerts={false}
                          showPreviewsInDropzone={false}
                          showFileNames={false}
                          Icon={(props) => (
                            <CloudUploadIcon
                              style={{
                                color: "#1d1a57",
                                width: 35,
                                height: 35,
                              }}
                              {...props}
                            />
                          )}
                          dropzoneText={
                            <Typography
                              sx={{
                                color: "#1d1a57",
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              Upload / Drag files
                            </Typography>
                          }
                          previewText="Selected Person Image:"
                        />
                      </Grid>
                      <Grid
                        sx={{
                          my: 3,
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: "auto",
                        }}
                      >
                        {selectedPersonImage && (
                          <img
                            src={selectedPersonImage}
                            alt="PersonImage"
                            style={{ maxWidth: "90px" }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 1 }} />
                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Divider sx={{ mb: 1 }} />
                  <Grid
                    container
                    spacing={isMobileScreen ? 0 : 2}
                    md={12}
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <Button
                      color="success"
                      sx={{
                        mr: 4,
                        my: 2,
                        px: 2,
                        fontSize: 12,
                        background: "#1d1a57",
                        color: "#FFF",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#1d1a57",
                          color: "#FFF",
                        },
                      }}
                      onClick={handlePeopleSubmit}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Save Changes"}
                    </Button>
                    <Button
                      color="secondary"
                      sx={{
                        my: 2,
                        px: 2,
                        fontSize: 12,
                        background: "#6c757d",
                        color: "#FFF",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#5a6268",
                          color: "#FFF",
                        },
                      }}
                      onClick={() => navigate("/people-manager/people-list")}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "auto" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbarSeverity}
                sx={{ width: "auto" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default EditPeople;